import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API } from '@/App';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { CreditCard, Upload, CheckCircle, Loader2, ArrowLeft, Gift, Copy, FileImage, Clock, XCircle, ChevronDown, ChevronUp } from 'lucide-react';

function BuyVotesPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [proofFile, setProofFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [myPayments, setMyPayments] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  const votePackages = [
    { id: 1, votes: 10, price: 10, popular: false },
    { id: 2, votes: 25, price: 25, popular: false },
    { id: 3, votes: 50, price: 45, popular: true, savings: '10%' },
    { id: 4, votes: 100, price: 80, popular: false, savings: '20%' },
    { id: 5, votes: 250, price: 175, popular: false, savings: '30%' },
    { id: 6, votes: 500, price: 300, popular: false, savings: '40%' },
  ];

  useEffect(() => {
    fetchUser();
    fetchMyPayments();
  }, []);

  const fetchUser = async () => {
    try {
      const response = await fetch(`${API}/auth/me`, { credentials: 'include' });
      if (!response.ok) { navigate('/login'); return; }
      setUser(await response.json());
    } catch { navigate('/login'); }
    finally { setIsLoading(false); }
  };

  const fetchMyPayments = async () => {
    try {
      const response = await fetch(`${API}/payments/my-submissions`, { credentials: 'include' });
      if (response.ok) setMyPayments(await response.json());
    } catch { /* ignore */ }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const allowed = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'application/pdf'];
    if (!allowed.includes(file.type)) {
      toast.error('Please upload an image (JPG, PNG, WebP, GIF) or PDF');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File must be under 10MB');
      return;
    }
    setProofFile(file);
  };

  const handleSubmitPayment = async () => {
    if (!selectedPackage) { toast.error('Please select a vote package'); return; }
    if (!proofFile) { toast.error('Please upload proof of payment'); return; }

    setIsSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('file', proofFile);
      
      const response = await fetch(
        `${API}/payments/submit?votes_count=${selectedPackage.votes}&amount_paid=${selectedPackage.price}`,
        { method: 'POST', credentials: 'include', body: formData }
      );

      if (response.ok) {
        toast.success('Payment proof submitted! Our team will verify within 24 hours.');
        setSelectedPackage(null);
        setProofFile(null);
        fetchMyPayments();
        fetchUser();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to submit payment');
      }
    } catch { toast.error('Failed to submit payment'); }
    finally { setIsSubmitting(false); }
  };

  const statusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-500/20 text-yellow-400',
      approved: 'bg-green-500/20 text-green-400',
      rejected: 'bg-red-500/20 text-red-400',
    };
    return <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || ''}`}>{status}</span>;
  };

  if (isLoading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  }

  return (
    <div className="min-h-screen pb-24">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-6" data-testid="back-btn">
          <ArrowLeft className="mr-2 h-4 w-4" /> Back
        </Button>

        {/* Header */}
        <div className="text-center mb-10">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-green-400 to-emerald-500 bg-clip-text text-transparent">
            Buy Votes
          </h1>
          <p className="text-lg text-muted-foreground">Support your favorite artists with additional votes</p>
          <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-blue-900 rounded-full">
            <Gift className="h-5 w-5 text-secondary" />
            <span>Your Balance: <strong className="text-secondary">{user?.free_votes_remaining || 0}</strong> votes</span>
          </div>
        </div>

        {/* Vote Packages */}
        <div className="mb-10">
          <h2 className="text-lg md:text-xl font-bold mb-6 text-center">Select a Package</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {votePackages.map((pkg) => (
              <Card
                key={pkg.id}
                onClick={() => setSelectedPackage(pkg)}
                className={`p-6 cursor-pointer transition-all hover:scale-105 relative ${
                  selectedPackage?.id === pkg.id ? 'border-2 border-green-500 bg-green-500/10' : 'glass-panel hover:border-green-500/50'
                } ${pkg.popular ? 'ring-2 ring-yellow-500' : ''}`}
                data-testid={`vote-package-${pkg.votes}`}
              >
                {pkg.popular && <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-yellow-500 text-black text-xs font-bold rounded-full">POPULAR</div>}
                {pkg.savings && <div className="absolute -top-3 right-2 px-2 py-1 bg-green-500 text-white text-xs font-bold rounded-full">Save {pkg.savings}</div>}
                <div className="text-center">
                  <p className="text-4xl font-bold text-green-400 mb-2">{pkg.votes}</p>
                  <p className="text-sm text-muted-foreground mb-3">votes</p>
                  <p className="text-2xl font-bold">R{pkg.price}</p>
                  <p className="text-xs text-muted-foreground mt-1">R{(pkg.price / pkg.votes).toFixed(2)}/vote</p>
                </div>
                {selectedPackage?.id === pkg.id && <div className="absolute top-2 right-2"><CheckCircle className="h-6 w-6 text-green-500" /></div>}
              </Card>
            ))}
          </div>
        </div>

        {/* Payment Details */}
        {selectedPackage && (
          <Card className="glass-panel p-6 mb-8" data-testid="payment-details-card">
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
              <CreditCard className="h-5 w-5" /> Payment Details
            </h2>
            
            <div className="bg-blue-900/50 p-4 rounded-xl mb-6">
              <p className="font-bold mb-3">Bank Transfer Details:</p>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between items-center">
                  <p><span className="text-muted-foreground">Bank:</span> Capitec Business Bank</p>
                </div>
                <div className="flex justify-between items-center">
                  <p><span className="text-muted-foreground">Account Name:</span> Tsarona Talent Show</p>
                </div>
                <div className="flex justify-between items-center">
                  <p><span className="text-muted-foreground">Account Number:</span> <span className="font-mono">1052 747 299</span></p>
                  <button onClick={() => { navigator.clipboard.writeText('1052747299'); toast.success('Account number copied!'); }}
                    className="text-xs bg-white/10 hover:bg-white/20 px-2 py-1 rounded flex items-center gap-1 transition-colors" data-testid="copy-account-btn">
                    <Copy className="h-3 w-3" /> Copy
                  </button>
                </div>
                <div className="flex justify-between items-center">
                  <p><span className="text-muted-foreground">Branch Code:</span> <span className="font-mono">470010</span></p>
                  <button onClick={() => { navigator.clipboard.writeText('470010'); toast.success('Branch code copied!'); }}
                    className="text-xs bg-white/10 hover:bg-white/20 px-2 py-1 rounded flex items-center gap-1 transition-colors" data-testid="copy-branch-btn">
                    <Copy className="h-3 w-3" /> Copy
                  </button>
                </div>
                <div className="flex justify-between items-center">
                  <p><span className="text-muted-foreground">Reference:</span> <span className="text-yellow-400 font-medium">{user?.email || user?.user_id}</span></p>
                  <button onClick={() => { navigator.clipboard.writeText(user?.email || user?.user_id); toast.success('Reference copied!'); }}
                    className="text-xs bg-white/10 hover:bg-white/20 px-2 py-1 rounded flex items-center gap-1 transition-colors" data-testid="copy-reference-btn">
                    <Copy className="h-3 w-3" /> Copy
                  </button>
                </div>
              </div>
              <button
                onClick={() => { 
                  const details = `Bank: Capitec Business Bank\nAccount Name: Tsarona Talent Show\nAccount Number: 1052747299\nBranch Code: 470010\nReference: ${user?.email || user?.user_id}`;
                  navigator.clipboard.writeText(details); toast.success('All banking details copied!'); 
                }}
                className="mt-4 w-full text-sm bg-primary/20 hover:bg-primary/30 text-primary py-2 rounded-lg flex items-center justify-center gap-2 transition-colors"
                data-testid="copy-all-btn">
                <Copy className="h-4 w-4" /> Copy All Details
              </button>
            </div>

            <div className="bg-green-500/10 p-4 rounded-xl mb-6 border border-green-500/30">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm text-muted-foreground">Selected Package</p>
                  <p className="text-2xl font-bold text-green-400">{selectedPackage.votes} Votes</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-muted-foreground">Amount</p>
                  <p className="text-2xl font-bold">R{selectedPackage.price}</p>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">
                  <Upload className="inline h-4 w-4 mr-2" />
                  Upload Proof of Payment
                </label>
                <div 
                  className={`border-2 border-dashed rounded-xl p-6 text-center transition-colors cursor-pointer ${
                    proofFile ? 'border-green-500 bg-green-500/5' : 'border-white/20 hover:border-primary/50'
                  }`}
                  onClick={() => document.getElementById('proof-file-input').click()}
                  data-testid="proof-upload-area"
                >
                  <input
                    id="proof-file-input"
                    type="file"
                    accept="image/*,.pdf"
                    onChange={handleFileChange}
                    className="hidden"
                    data-testid="proof-file-input"
                  />
                  {proofFile ? (
                    <div className="flex items-center justify-center gap-3">
                      <FileImage className="h-8 w-8 text-green-400" />
                      <div className="text-left">
                        <p className="font-medium text-green-400">{proofFile.name}</p>
                        <p className="text-xs text-muted-foreground">{(proofFile.size / 1024).toFixed(1)} KB</p>
                      </div>
                      <button 
                        onClick={(e) => { e.stopPropagation(); setProofFile(null); }}
                        className="ml-2 text-red-400 hover:text-red-300"
                        data-testid="remove-file-btn"
                      >
                        <XCircle className="h-5 w-5" />
                      </button>
                    </div>
                  ) : (
                    <>
                      <Upload className="h-10 w-10 text-muted-foreground mx-auto mb-2" />
                      <p className="text-sm text-muted-foreground">Click to upload screenshot or PDF</p>
                      <p className="text-xs text-muted-foreground mt-1">JPG, PNG, WebP, GIF, PDF (max 10MB)</p>
                    </>
                  )}
                </div>
              </div>

              <Button
                onClick={handleSubmitPayment}
                disabled={isSubmitting || !proofFile}
                className="w-full bg-green-500 hover:bg-green-600 text-white rounded-full py-6 font-bold text-lg"
                data-testid="submit-payment-btn"
              >
                {isSubmitting ? (
                  <><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Uploading...</>
                ) : (
                  <><CheckCircle className="mr-2 h-5 w-5" /> Submit Payment for Approval</>
                )}
              </Button>
            </div>
          </Card>
        )}

        {/* Payment History */}
        {myPayments.length > 0 && (
          <Card className="glass-panel p-6 mb-8" data-testid="payment-history-card">
            <button
              onClick={() => setShowHistory(!showHistory)}
              className="w-full flex items-center justify-between font-bold text-lg"
              data-testid="toggle-history-btn"
            >
              <span className="flex items-center gap-2"><Clock className="h-5 w-5" /> My Payment Submissions ({myPayments.length})</span>
              {showHistory ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
            </button>
            {showHistory && (
              <div className="mt-4 space-y-3">
                {myPayments.map((p) => (
                  <div key={p.payment_id} className="flex items-center justify-between p-3 bg-white/5 rounded-lg" data-testid={`payment-${p.payment_id}`}>
                    <div>
                      <p className="font-medium">{p.votes_to_add} votes - R{p.amount}</p>
                      <p className="text-xs text-muted-foreground">{new Date(p.created_at).toLocaleDateString()}</p>
                    </div>
                    {statusBadge(p.status)}
                  </div>
                ))}
              </div>
            )}
          </Card>
        )}

        {/* How it works */}
        <Card className="glass-panel p-6">
          <h3 className="font-bold mb-3">How it works:</h3>
          <ol className="space-y-2 text-sm text-muted-foreground">
            <li>1. Select your desired vote package above</li>
            <li>2. Make a bank transfer using the details provided</li>
            <li>3. Upload a screenshot of your proof of payment</li>
            <li>4. Our team will verify and credit your votes within 24 hours</li>
          </ol>
        </Card>
      </div>
    </div>
  );
}

export default BuyVotesPage;
